import { Role } from './enums';
/**
 * Authorisation is checked against *permissions*, never against role names.
 *
 * Guards ask "does this user hold `page:manage`?" rather than "is this user an
 * ADMIN?". Adding a role, or moving a capability between roles, therefore means
 * editing the matrix below and nothing else — no guard, controller or React
 * component needs to change. This is the single most important extensibility
 * decision in the codebase; see docs/RBAC.md.
 */
export const Permission = {
    // --- Blog content ---------------------------------------------------------
    POST_READ: 'post:read',
    POST_CREATE: 'post:create',
    /** Edit a post you authored. */
    POST_UPDATE_OWN: 'post:update:own',
    /** Edit anyone's post. */
    POST_UPDATE_ANY: 'post:update:any',
    POST_DELETE: 'post:delete',
    /** Move a post to PUBLISHED/SCHEDULED, or toggle it live. */
    POST_PUBLISH: 'post:publish',
    /** Change the public ordering of the blog listing. */
    POST_REORDER: 'post:reorder',
    // --- Taxonomy -------------------------------------------------------------
    TAXONOMY_MANAGE: 'taxonomy:manage',
    // --- Media ----------------------------------------------------------------
    MEDIA_READ: 'media:read',
    MEDIA_UPLOAD: 'media:upload',
    MEDIA_DELETE: 'media:delete',
    // --- Site structure -------------------------------------------------------
    // This is the area the client specifically asked to put behind role-based
    // access control: deciding which pages exist and which are exposed publicly.
    PAGE_READ: 'page:read',
    PAGE_MANAGE: 'page:manage',
    NAVIGATION_READ: 'navigation:read',
    NAVIGATION_MANAGE: 'navigation:manage',
    SETTINGS_READ: 'settings:read',
    SETTINGS_MANAGE: 'settings:manage',
    // --- Inquiries ------------------------------------------------------------
    INQUIRY_READ: 'inquiry:read',
    INQUIRY_MANAGE: 'inquiry:manage',
    // --- Team -----------------------------------------------------------------
    USER_READ: 'user:read',
    USER_INVITE: 'user:invite',
    USER_MANAGE: 'user:manage',
    /** Change somebody's role. Separated because it is privilege escalation. */
    ROLE_ASSIGN: 'role:assign',
    // --- Audit ----------------------------------------------------------------
    AUDIT_READ: 'audit:read',
};
export const ALL_PERMISSIONS = Object.values(Permission);
/** Everything an author can do: their own drafts, plus uploading images. */
const AUTHOR_PERMISSIONS = [
    Permission.POST_READ,
    Permission.POST_CREATE,
    Permission.POST_UPDATE_OWN,
    Permission.MEDIA_READ,
    Permission.MEDIA_UPLOAD,
];
/**
 * Editors own the blog end-to-end. Note what is absent: PAGE_MANAGE,
 * NAVIGATION_MANAGE, SETTINGS_MANAGE and everything under USER_*. An editor
 * cannot change which pages the site exposes — that was an explicit
 * requirement.
 */
const EDITOR_PERMISSIONS = [
    ...AUTHOR_PERMISSIONS,
    Permission.POST_UPDATE_ANY,
    Permission.POST_DELETE,
    Permission.POST_PUBLISH,
    Permission.POST_REORDER,
    Permission.TAXONOMY_MANAGE,
    Permission.MEDIA_DELETE,
    Permission.PAGE_READ,
    Permission.NAVIGATION_READ,
    Permission.SETTINGS_READ,
    Permission.INQUIRY_READ,
];
const ADMIN_PERMISSIONS = [
    ...EDITOR_PERMISSIONS,
    Permission.PAGE_MANAGE,
    Permission.NAVIGATION_MANAGE,
    Permission.SETTINGS_MANAGE,
    Permission.INQUIRY_MANAGE,
    Permission.USER_READ,
    Permission.USER_INVITE,
    Permission.AUDIT_READ,
];
/**
 * Only a super admin can manage team members' accounts or reassign roles.
 * This keeps privilege escalation to one accountable role.
 */
const SUPER_ADMIN_PERMISSIONS = [
    ...ADMIN_PERMISSIONS,
    Permission.USER_MANAGE,
    Permission.ROLE_ASSIGN,
];
export const ROLE_PERMISSIONS = {
    [Role.SUPER_ADMIN]: Object.freeze(SUPER_ADMIN_PERMISSIONS),
    [Role.ADMIN]: Object.freeze(ADMIN_PERMISSIONS),
    [Role.EDITOR]: Object.freeze(EDITOR_PERMISSIONS),
    [Role.AUTHOR]: Object.freeze(AUTHOR_PERMISSIONS),
};
export function permissionsForRole(role) {
    return ROLE_PERMISSIONS[role] ?? [];
}
export function roleHasPermission(role, permission) {
    return permissionsForRole(role).includes(permission);
}
/** True when the role holds every permission listed. */
export function roleHasAllPermissions(role, permissions) {
    const held = permissionsForRole(role);
    return permissions.every((p) => held.includes(p));
}
/** True when the role holds at least one of the permissions listed. */
export function roleHasAnyPermission(role, permissions) {
    const held = permissionsForRole(role);
    return permissions.some((p) => held.includes(p));
}
/**
 * Ownership-aware check for the common "edit a post" case.
 *
 * Collapsing this into one helper keeps the rule in a single place instead of
 * being re-derived — subtly differently — in each controller.
 */
export function canEditPost(role, actorId, post) {
    if (roleHasPermission(role, Permission.POST_UPDATE_ANY))
        return true;
    return roleHasPermission(role, Permission.POST_UPDATE_OWN) && post.authorId === actorId;
}
//# sourceMappingURL=permissions.js.map